/** Queue cards shown on Job Queue page */
export const jobQueueCards = [
  {
    key: 'attendee-sync',
    name: 'attendee-sync',
    status: 'running',
    depth: 129,
    throughputPerMin: 18,
    successRate: 94,
  },
  {
    key: 'meeting-sync',
    name: 'meeting-sync',
    status: 'paused',
    depth: 44,
    throughputPerMin: 9,
    successRate: 91,
  },
  {
    key: 'transcript',
    name: 'transcript',
    status: 'running',
    depth: 312,
    throughputPerMin: 42,
    successRate: 97,
  },
  {
    key: 'notifications',
    name: 'notifications',
    status: 'error',
    depth: 18,
    throughputPerMin: 6,
    successRate: 78,
  },
];

const QUEUES = jobQueueCards.map((q) => q.key);
const TYPES = ['sync_attendees', 'sync_meeting', 'transcribe_chunk', 'push_notification', 'calendar_webhook'];

export const platformJobsList = Array.from({ length: 48 }, (_, i) => {
  const q = QUEUES[i % 4];
  const failed = i % 11 === 0;
  const running = i % 13 === 0 && !failed;
  const created = new Date(Date.now() - i * 120000);
  const started = running || (!failed && i % 2 === 0) ? new Date(created.getTime() + 5000) : failed ? new Date(created.getTime() + 8000) : new Date(created.getTime() + 3000);
  const status = failed ? 'failed' : running ? 'running' : i % 19 === 0 ? 'queued' : 'success';
  const durationMs =
    status === 'running' ? null : status === 'queued' ? null : failed ? null : 1200 + (i % 7) * 400;
  return {
    id: `job-${9000 + i}`,
    queue: q,
    tenantId: `tnt-${1000 + (i % 24)}`,
    tenantCode: `MH-${String(1000 + (i % 24)).padStart(4, '0')}`,
    companyName: `Tenant Co ${(i % 24) + 1}`,
    type: TYPES[i % TYPES.length],
    provider: ['microsoft', 'google', 'whatsapp', 'internal'][i % 4],
    createdAt: created.toISOString(),
    startedAt: started.toISOString(),
    status,
    durationMs,
    attempts: failed ? 3 : 1,
    errorCode: failed ? (i % 2 === 0 ? '401_invalid_grant' : 'TIMEOUT') : null,
    errorMessage: failed
      ? i % 2 === 0
        ? 'OAuth token refresh failed: invalid_grant'
        : 'Upstream request timed out after 30s'
      : null,
  };
});

export const jobSuggestedFixByErrorCode = {
  '401_invalid_grant': {
    text: 'Microsoft token expired. Navigate to tenant integrations and request admin to re-consent.',
    runbookUrl: 'https://docs.meetinghub.ai/runbooks/oauth-reconsent',
    integrationProvider: 'microsoft',
  },
  TIMEOUT: {
    text: 'Increase provider timeout or retry during off-peak. Check connector health board for incidents.',
    runbookUrl: 'https://docs.meetinghub.ai/runbooks/webhook-timeouts',
    integrationProvider: null,
  },
  RATE_LIMIT: {
    text: 'Apply exponential backoff and reduce parallel job fan-out for this tenant.',
    runbookUrl: 'https://docs.meetinghub.ai/runbooks/rate-limits',
    integrationProvider: null,
  },
};

export function buildJobDetail(jobId) {
  const row = platformJobsList.find((j) => j.id === jobId) || platformJobsList[0];
  const code = row.errorCode || 'TIMEOUT';
  const fix = jobSuggestedFixByErrorCode[code] || jobSuggestedFixByErrorCode.TIMEOUT;
  const payload = {
    meetingId: `mtg-${row.id}`,
    tenantId: row.tenantId,
    correlationId: `corr-${row.id}`,
    options: { strict: true },
  };
  return {
    ...row,
    summary: {
      jobId: row.id,
      queue: row.queue,
      tenantId: row.tenantId,
      tenantCode: row.tenantCode,
      provider: row.provider,
      type: row.type,
      createdAt: row.createdAt,
      attempts: row.attempts,
      status: row.status,
    },
    error: {
      code,
      message: row.errorMessage || 'Simulated failure for demonstration',
    },
    payload,
    payloadJson: JSON.stringify(payload, null, 2),
    stackTrace: `Error: ${row.errorMessage || code}\n    at Worker.execute (/app/workers/${row.queue}.js:142:11)\n    at processTicksAndRejections (node:internal/process/task_queues:95:5)\n    at async QueueRunner.run (/app/queue/runner.js:88:3)`,
    retryHistory: [
      {
        attempt: 1,
        ts: new Date(new Date(row.createdAt).getTime() + 10000).toISOString(),
        error: 'Connection reset',
        durationMs: 2100,
        detail: 'ECONNRESET from provider',
      },
      {
        attempt: 2,
        ts: new Date(new Date(row.createdAt).getTime() + 40000).toISOString(),
        error: row.errorMessage || code,
        durationMs: 30500,
        detail: row.errorMessage || 'Final failure',
      },
    ],
    suggestedFix: fix,
  };
}

/** Retry center rows */
export const retryCenterJobs = Array.from({ length: 24 }, (_, i) => {
  const idempotent = i % 5 !== 0;
  const failed = new Date(Date.now() - (i + 1) * 3600000).toISOString();
  return {
    id: `rj-${i}`,
    jobId: `job-${9200 + i}`,
    queue: QUEUES[i % 4],
    tenantId: `tnt-${1100 + (i % 18)}`,
    tenantCode: `MH-${String(2100 + (i % 18)).padStart(4, '0')}`,
    errorSnippet: i % 3 === 0 ? 'invalid_grant — token refresh failed' : '503 Service Unavailable from graph',
    failedAt: failed,
    attempts: 2 + (i % 3),
    retryable: idempotent ? 'yes' : 'force',
    idempotent,
    provider: ['microsoft', 'google', 'whatsapp'][i % 3],
    status: i % 7 === 0 ? 'scheduled' : 'failed',
  };
});

/** Legacy exports for older mock consumers */
export const jobQueues = jobQueueCards.map((c) => ({
  name: c.name,
  depth: c.depth,
  consumers: 4 + (c.depth % 8),
  dlqDepth: c.status === 'error' ? 3 : 0,
}));

export const jobsList = platformJobsList;

export const failureDetails = {
  topErrors: [
    { code: '401_invalid_grant', count: 28 },
    { code: 'TIMEOUT', count: 42 },
    { code: 'RATE_LIMIT', count: 18 },
  ],
  affectedTenants24h: 7,
};

export const retryCenterItems = retryCenterJobs;
